// Thin API client. All requests include credentials so the bloom_session
// cookie travels both ways. Throws on non-2xx with a parsed error code +
// message when the server returns the standard {error: {code, message}}
// envelope.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function call<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  const resp = await fetch(path, {
    ...init,
    credentials: 'include',
    headers,
  });
  const text = await resp.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); } catch { /* non-JSON */ }
  }
  if (!resp.ok) {
    const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      resp.status,
      err?.code ?? `http_${resp.status}`,
      err?.message ?? `Request failed: ${resp.status}`,
    );
  }
  return (body as T) ?? ({} as T);
}

export const api = {
  get: <T>(path: string): Promise<T> => call<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown): Promise<T> => call<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  }),
  delete: <T>(path: string): Promise<T> => call<T>(path, { method: 'DELETE' }),
};

// ── Auth endpoints ──────────────────────────────────────────────────

export interface UserAccountAccess {
  account_id: string;
  account_name: string;
  role: 'owner' | 'member';
}

export interface SessionInfo {
  user: {
    id: string;
    email: string;
    role: 'owner' | 'member' | 'operator';
    account_id: string | null;
    display_name: string | null;
  };
  /** Accounts this user has access to. Empty for operator (operator
   *  scope is "all accounts" — see portfolio endpoint instead). */
  accounts: UserAccountAccess[];
  session: {
    id: string;
    origin: 'magic_link' | 'passkey';
    created_at: string;
    last_used_at: string;
    expires_at: string;
  };
}

export interface PasskeyListItem {
  credential_id: string;
  registered_at: string;
  last_used_at: string | null;
  registered_user_agent: string | null;
  transports: string[];
}

export const auth = {
  me: (): Promise<SessionInfo> => api.get<SessionInfo>('/api/dashboard/auth/me'),
  requestMagicLink: (email: string, redirect_to?: string): Promise<{ ok: true }> =>
    api.post('/api/dashboard/auth/magic-link/request', { email, redirect_to }),
  logout: (): Promise<{ ok: true }> => api.post('/api/dashboard/auth/logout'),
  listPasskeys: (): Promise<{ credentials: PasskeyListItem[] }> =>
    api.get('/api/dashboard/auth/passkey/list'),
  passkeyAuthenticateBegin: (email?: string): Promise<{ challenge_id: string; options: unknown }> =>
    api.post('/api/dashboard/auth/passkey/authenticate/begin', email ? { email } : {}),
  passkeyAuthenticateFinish: (challenge_id: string, response: unknown): Promise<{ ok: true }> =>
    api.post('/api/dashboard/auth/passkey/authenticate/finish', { challenge_id, response }),
  passkeyRegisterBegin: (): Promise<{ challenge_id: string; options: unknown }> =>
    api.post('/api/dashboard/auth/passkey/register/begin'),
  passkeyRegisterFinish: (challenge_id: string, response: unknown): Promise<{ credential_id: string; registered_at: string }> =>
    api.post('/api/dashboard/auth/passkey/register/finish', { challenge_id, response }),
};

// ── Operator endpoints ──────────────────────────────────────────────

export interface OperatorCustomerRow {
  account_id: string;
  display_name: string;
  vertical: string;
  city: string | null;
  state: string | null;
  status: string;
  created_at: string;
  qa_regressions_24h: number;
  last_db_backup_status: string | null;
  last_db_backup_at: string | null;
  content_published_7d: number;
  last_lighthouse_perf: number | null;
  primary_owner_email: string | null;
}

export interface OperatorAutoreasonRunSummary {
  run_id: string;
  target_url: string;
  target_query: string;
  page_type: 'service' | 'tool' | 'programmatic' | 'aieo';
  status: string;
  started_at: string;
  cost_usd: number;
  iteration_count: number;
  latest_drift_score: number | null;
  latest_amend_directive: string | null;
  publish_state:
    | 'pending_operator_confirmation'
    | 'published'
    | 'publish_failed'
    | 'verify_failed'
    | 'rejected'
    | null;
}

export interface OperatorAutoreasonIteration {
  iteration_num: number;
  drift_score: number | null;
  drift_examples: string[];
  amend_directive: string | null;
  iteration_cost_usd: number;
  duration_ms: number;
  prohibition_violations_count: number;
  improvement_pct: number | null;
}

export interface OperatorAutoreasonRunDetail extends OperatorAutoreasonRunSummary {
  account_id: string | null;
  initial_content_hash: string;
  initial_content_excerpt: string;
  final_content_hash: string | null;
  final_content_excerpt: string | null;
  iterations: OperatorAutoreasonIteration[];
  publish_committed_sha: string | null;
  publish_diff_excerpt: string | null;
  publish_actor_kind: string | null;
  published_at: string | null;
  verified_at: string | null;
  publish_error: string | null;
  publish_rejected_reason: string | null;
}

export const operator = {
  customers: (): Promise<{ customers: OperatorCustomerRow[] }> =>
    api.get('/api/dashboard/operator/customers'),
  autoreasonRuns: (): Promise<{ runs: OperatorAutoreasonRunSummary[] }> =>
    api.get('/api/dashboard/operator/autoreason/runs'),
  autoreasonRun: (id: string): Promise<{ run: OperatorAutoreasonRunDetail }> =>
    api.get(`/api/dashboard/operator/autoreason/runs/${encodeURIComponent(id)}`),
};

// ── Integrations endpoints ──────────────────────────────────────────

export interface IntegrationsStatus {
  gbp:
    | { connected: false }
    | {
        connected: true;
        account_id_remote: string;
        location_id_remote: string;
        scopes: string[];
        oauth_completed_at: string;
        last_refreshed_at: string | null;
      };
  gsc:
    | { connected: false }
    | {
        connected: true;
        property_url: string;
        scopes: string[];
        oauth_completed_at: string;
        last_refreshed_at: string | null;
      };
  ga4:
    | { connected: false }
    | {
        connected: true;
        property_id: string;
        verified: boolean;
        last_verified_at: string | null;
        last_verify_error: string | null;
      };
  /** SA email customer pastes into GA4 admin. Null when GA4 SA not
   *  configured server-side. */
  ga4_service_account_email: string | null;
}

export interface Ga4ConnectResultSuccess {
  connected: true;
  verified: true;
  property_id: string;
  display_name: string;
}

export interface Ga4ConnectResultPending {
  connected: true;
  verified: false;
  property_id: string;
  reason: string;
  detail: string;
  ga4_service_account_email: string;
  hint: string | null;
}

export type Ga4ConnectResult = Ga4ConnectResultSuccess | Ga4ConnectResultPending;

export const integrations = {
  status: (): Promise<IntegrationsStatus> =>
    api.get('/api/dashboard/integrations/status'),
  prepareGoogle: (integration: 'gbp' | 'gsc'): Promise<{ authorize_url: string }> =>
    api.post('/api/dashboard/oauth/google/prepare', { integration }),
  connectGa4: (property_id: string): Promise<Ga4ConnectResult> =>
    api.post('/api/dashboard/integrations/ga4/connect', { property_id }),
  disconnect: (kind: 'gbp' | 'gsc' | 'ga4'): Promise<{ disconnected: string }> =>
    api.delete(`/api/dashboard/integrations/${kind}`),
};

// ── CRM connection endpoints (review-solicitation ingestion) ────────

/** Providers a contractor can self-connect today (mirrors the server's
 *  SELF_SERVE_PROVIDERS allowlist). Native OAuth providers join as their
 *  sandbox-verified mappers land. */
export type CrmProvider = 'generic_webhook' | 'zapier';

export interface CrmConnectionSummary {
  id: string;
  provider: string;
  auth_method: string;
  status: 'pending' | 'connected' | 'fixture' | 'revoked' | 'error';
  external_ref: string | null;
  consent_attested_at: string | null;
  created_at: string;
}

export interface CrmConnectResult {
  connection_id: string;
  /** The URL to POST customer events to. Contains a one-time routing token. */
  webhook_url: string;
  /** The HMAC signing secret — shown ONCE; not retrievable from status. */
  signing_secret: string;
  provider: string;
}

export const crm = {
  status: (): Promise<{ connections: CrmConnectionSummary[] }> =>
    api.get('/api/dashboard/crm/status'),
  connect: (provider: CrmProvider, external_ref: string): Promise<CrmConnectResult> =>
    api.post('/api/dashboard/crm/connect', {
      provider,
      external_ref,
      attestation_accepted: true,
    }),
  disconnect: (connection_id: string): Promise<{ disconnected: true; suppressed: number }> =>
    api.post('/api/dashboard/crm/disconnect', { connection_id }),
};
